// ============================================================================
// BESPOKIAN Health - Employers Page
// B2B benefits page with luxury styling
// ============================================================================

import { useState } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Users, DollarSign, Shield, TrendingDown, Building2, CheckCircle2, BarChart3, MessageCircle, ChevronDown } from 'lucide-react';

const employerFaqs = [
  {
    question: 'How does BESPOKIAN pricing work for employers?',
    answer: 'We charge actual drug cost plus 15% (capped at $300) and a flat $5 dispensing fee per prescription. No PBM spread pricing or hidden rebates—your plan and employees see the same transparent price.',
  },
  {
    question: 'Do you accept our existing insurance or PBM?',
    answer: 'We are built to work alongside or replace traditional PBM arrangements. Our platform can compare our transparent cash price to your current copays so employees automatically get the better option.',
  },
  {
    question: 'How do we roll this out to our employees?',
    answer: 'We provide a simple enrollment path, dedicated account support, and clear communications so employees understand the model. Prescription transfers and new prescriptions are handled seamlessly.',
  },
  {
    question: 'Are you licensed and compliant?',
    answer: 'Bespokian Health is in the licensing phase for all 50 states. We follow HIPAA-compliant protocols and every prescription is reviewed by a licensed pharmacist.',
  },
];

const benefits = [
  {
    icon: DollarSign,
    title: 'Save 30-50%',
    description: 'On prescription drug costs with our transparent pricing model',
  },
  {
    icon: Users,
    title: 'Happier Employees',
    description: 'Clear pricing they understand and trust',
  },
  {
    icon: Shield,
    title: 'Fully Compliant',
    description: 'HIPAA-compliant protocols, licensing in progress',
  },
  {
    icon: BarChart3,
    title: 'Clear Reporting',
    description: 'Real-time dashboards and cost analytics',
  },
];

const features = [
  'Transparent drug cost + 15% + $5 model',
  'No PBM spread pricing or rebate games',
  'Dedicated account manager',
  'Licensed pharmacist support for employees',
  'Free 2-3 day delivery nationwide',
  'Seamless prescription transfers',
  'Easy integration with existing benefits',
  'Real-time utilization reporting',
];

export function EmployersPage() {
  const [faqSectionOpen, setFaqSectionOpen] = useState(false);
  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null);

  const scrollToContent = () => {
    const contentSection = document.getElementById('employers-content');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1">
        {/* Hero Section with Chemistry Lab Background */}
        <section
          className="relative h-screen flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 21, 58, 0.55), rgba(0, 21, 58, 0.9)), url("/img/heroes/health-employers-hero.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center center'
          }}
        >
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-8" />
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 md:mb-8 leading-tight tracking-tight">
              For Employers
            </h1>
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-12" />

            {/* Learn More - Scroll to content section */}
            <div className="max-w-xl mx-auto">
              <div
                id="learn-more"
                role="button"
                tabIndex={0}
                onClick={scrollToContent}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') scrollToContent();
                }}
                className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-4 border border-white/20 shadow-xl ring-1 ring-white/10 cursor-pointer hover:bg-white/20 transition-all duration-300 w-full"
                aria-label="Learn more about employer benefits"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="text-left">
                    <div className="text-white/90 text-xs uppercase tracking-widest mb-2">
                      LEARN MORE
                    </div>
                    <div className="text-white/70 text-lg">
                      See how it works
                    </div>
                  </div>
                  <MessageCircle className="h-6 w-6 text-white/80 flex-shrink-0" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Employers Content Section */}
        <div id="employers-content">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-0.5 bg-gold-500 mb-6" />

                <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
                  For Employers
                </p>

                <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
                  Better Pharmacy Benefits, Lower Costs
                </h1>

                <p className="text-xl text-navy-600 mb-8">
                  Offer your employees transparent prescription pricing while reducing your healthcare spend by 30-50%. 
                  No PBM games. Just honest pharmacy care.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <button className="px-8 py-4 text-lg font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg shadow-lg transition-all hover:shadow-glow-gold inline-flex items-center justify-center gap-2">
                      Talk to Sales <ArrowRight className="h-5 w-5" />
                    </button>
                  </Link>
                  <a href="tel:1-877-237-7679">
                    <button className="px-8 py-4 text-lg font-medium text-navy-700 border border-navy-200 hover:bg-navy-50 rounded-lg transition-colors">
                      Call 1-877-BES-PORX
                    </button>
                  </a>
                </div>
              </div>

              {/* Stats Card */}
              <div className="bg-navy-800 rounded-2xl p-8 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="h-6 w-6 text-gold-400" />
                  <span className="font-serif text-xl italic">Employer Benefits</span>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="text-center p-4 bg-navy-700/50 rounded-xl">
                    <div className="flex items-center justify-center gap-1 text-3xl font-bold text-gold-400 mb-1">
                      <TrendingDown className="h-6 w-6" />
                      30-50%
                    </div>
                    <p className="text-navy-300 text-sm">Cost Reduction</p>
                  </div>
                  <div className="text-center p-4 bg-navy-700/50 rounded-xl">
                    <p className="text-3xl font-bold text-sage-400 mb-1">100%</p>
                    <p className="text-navy-300 text-sm">Price Transparency</p>
                  </div>
                </div>

                <p className="text-navy-200 text-sm">
                  Our simple pricing model eliminates PBM complexity and passes real savings to you and your employees.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">Why Companies Choose BESPOKIAN</h2>
              <p className="text-navy-600 max-w-2xl mx-auto">
                Transform your pharmacy benefits with transparent pricing that works for everyone.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit) => {
                const bgImage =
                  benefit.title === 'Save 30-50%'
                    ? '/img/save-30-50-bg.jpg'
                    : benefit.title === 'Happier Employees'
                      ? '/img/happier-employees-bg.jpg'
                      : benefit.title === 'Clear Reporting'
                        ? '/img/clear-reporting-bg.jpg'
                        : benefit.title === 'Fully Compliant'
                          ? '/img/fully-compliant-bg.jpg'
                          : null;
                const hasImageBg = !!bgImage;
                return (
                  <div
                    key={benefit.title}
                    className="rounded-xl p-6 border border-navy-100 hover:shadow-card transition-shadow relative overflow-hidden bg-navy-50 text-center"
                    style={
                      hasImageBg
                        ? {
                            backgroundImage: `linear-gradient(rgba(0, 21, 58, 0.75), rgba(0, 21, 58, 0.85)), url("${bgImage}")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }
                        : undefined
                    }
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 relative z-10 mx-auto ${
                        hasImageBg ? 'bg-gold-400/20' : 'bg-gold-100'
                      }`}
                    >
                      <benefit.icon
                        className={`h-6 w-6 ${hasImageBg ? 'text-gold-300' : 'text-gold-600'}`}
                      />
                    </div>
                    <h3
                      className={`font-semibold mb-2 relative z-10 ${
                        hasImageBg ? 'text-white' : 'text-navy-800'
                      }`}
                    >
                      {benefit.title}
                    </h3>
                    <p
                      className={`text-sm relative z-10 ${
                        hasImageBg ? 'text-navy-200' : 'text-navy-600'
                      }`}
                    >
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Collapsible FAQ - subtle, space-efficient */}
        <section className="py-8 bg-white border-t border-navy-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setFaqSectionOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-4 py-3 text-left text-navy-700 hover:text-navy-800 transition-colors"
              aria-expanded={faqSectionOpen}
            >
              <span className="font-display text-lg italic text-navy-800">Common questions</span>
              <ChevronDown
                className={`h-5 w-5 text-gold-600 transition-transform flex-shrink-0 ${faqSectionOpen ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
            {faqSectionOpen && (
              <div className="pt-2 space-y-2">
                {employerFaqs.map((faq, index) => {
                  const isOpen = faqOpenIndex === index;
                  return (
                    <div
                      key={index}
                      className={`rounded-lg border overflow-hidden transition-colors ${
                        isOpen ? 'border-gold-200 bg-gold-50/40' : 'border-navy-100 hover:border-navy-200'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setFaqOpenIndex((i) => (i === index ? null : index))}
                        className="w-full px-4 py-3 text-left flex items-center justify-between gap-3"
                      >
                        <span className="text-sm font-medium text-navy-800">{faq.question}</span>
                        <ChevronDown
                          className={`h-4 w-4 text-navy-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          aria-hidden
                        />
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3">
                          <p className="text-navy-600 text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-navy-500 text-xs pt-2">
                  More questions? <Link to="/faq" className="text-gold-600 hover:text-gold-700 font-medium">See full FAQ</Link>
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-navy-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-12 h-0.5 bg-gold-500 mb-10" />
                <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-6">
                  What's Included
                </h2>
                <p className="text-navy-600 mb-8">
                  A complete pharmacy benefits solution without the complexity of traditional PBMs.
                </p>

                <div className="grid sm:grid-cols-2 gap-3">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-sage-600 mt-0.5 flex-shrink-0" />
                      <span className="text-navy-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-white rounded-2xl shadow-card p-8 text-center">
                <div className="w-16 h-16 bg-gold-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Building2 className="h-8 w-8 text-gold-600" />
                </div>
                <h3 className="font-display text-2xl text-navy-800 tracking-tight italic mb-4">
                  Ready to Transform Your Benefits?
                </h3>
                <p className="text-navy-600 mb-6">
                  Schedule a demo to see how BESPOKIAN can reduce your pharmacy costs while improving employee satisfaction.
                </p>
                <Link to="/contact">
                  <button className="w-full px-6 py-3.5 font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg transition-all hover:shadow-glow-gold">
                    Request a Demo
                  </button>
                </Link>
                <p className="text-navy-500 text-sm mt-4">
                  Or call <a href="tel:1-877-237-7679" className="text-gold-600 hover:text-gold-700">1-877-BES-PORX</a>
                </p>
              </div>
            </div>
          </div>
        </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

